import { useMemo } from 'react';
import { useParams, Link } from 'wouter';
import { Navbar } from '@/components/layout/Navbar';
import { SEO } from '@/components/SEO';
import { TabContentRenderer } from '@/components/viewers/TabContentRenderer';
import { useCMS } from '@/contexts/CMSContext';
import NotFound from '@/pages/NotFound';
import { ChevronLeft, CheckCircle } from 'lucide-react';

const FOLDER_DISPLAY: Record<string, string> = {
  'about-me': 'About',
  'services': 'Services',
  'insights': 'Insights',
};

const SHOW_VERIFIED_STAMP: Record<string, boolean> = {
  'insights': true,
  'services': true,
  'about-me': false,
};

function formatVerifiedDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function TabDetail() {
  const params = useParams<{ folderSlug: string; tabSlug: string }>();
  const { tabs, isLoading } = useCMS();

  const folderSlug = params.folderSlug;
  const tabSlug = params.tabSlug;

  const tab = useMemo(() => {
    if (!tabSlug) return null;
    const t = tabs.find(t => t.slug === tabSlug && t.is_visible);
    if (!t) return null;
    if (t.page_slug !== folderSlug) return null;
    return t;
  }, [tabs, tabSlug, folderSlug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">Loading...</div>
      </div>
    );
  }

  if (!tab || !FOLDER_DISPLAY[folderSlug]) {
    return <NotFound />;
  }

  const folderLabel = FOLDER_DISPLAY[folderSlug];
  const canonicalUrl = `https://carlorbiz.com.au/${folderSlug}/${tabSlug}`;
  const ogImage = tab.og_image_url || undefined;

  const jsonLd: Record<string, unknown>[] = [];
  if (folderSlug === 'insights' && tab.content && tab.content.length > 200) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: tab.label,
      description: tab.summary || tab.label,
      author: {
        "@type": "Person",
        name: "Carla Taylor",
        url: "https://carlorbiz.com.au/about-me",
        jobTitle: "Strategic Consultant & Founder",
      },
      publisher: {
        "@type": "Organization",
        name: "Carlorbiz",
        url: "https://carlorbiz.com.au",
        logo: { "@type": "ImageObject", url: "https://carlorbiz.com.au/og-image.png" },
      },
      url: canonicalUrl,
      datePublished: tab.created_at || "2026-04-01",
      dateModified: tab.updated_at || tab.created_at || "2026-04-01",
      mainEntityOfPage: canonicalUrl,
      image: ogImage || "https://carlorbiz.com.au/og-image.png",
    });
  } else if (folderSlug === 'services') {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: tab.label,
      description: tab.summary || tab.label,
      url: canonicalUrl,
      provider: {
        "@type": "Person",
        name: "Carla Taylor",
        url: "https://carlorbiz.com.au/about-me",
        jobTitle: "Strategic Consultant & Founder",
      },
      areaServed: "AU",
      image: ogImage || "https://carlorbiz.com.au/og-image.png",
    });
  }

  const showVerifiedStamp = SHOW_VERIFIED_STAMP[folderSlug] && tab.last_verified_at;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={`${tab.label} — ${folderLabel} | Carlorbiz`}
        description={tab.summary || `${tab.label} — Carlorbiz ${folderLabel.toLowerCase()}`}
        canonicalUrl={canonicalUrl}
        ogImage={ogImage}
        type={folderSlug === 'insights' ? 'article' : 'website'}
        jsonLd={jsonLd.length > 0 ? jsonLd : undefined}
      />
      <Navbar />

      <main className="flex-1 w-full bg-[#F9F9F9]">
        <section className="bg-white text-[var(--color-brand-dark)] py-12 lg:py-16 relative overflow-hidden border-b border-gray-100">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[var(--color-brand-primary)] to-transparent rounded-full opacity-5 blur-3xl pointer-events-none transform translate-x-1/2 -translate-y-1/2" />

          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <Link
              href={`/${folderSlug}`}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#2D7E32] font-body transition-colors mb-6 group"
            >
              <ChevronLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
              Back to {folderLabel}
            </Link>

            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-sm">
              <span className="text-[#2D7E32]">{tab.label.split(' ')[0]}</span>
              {tab.label.split(' ').length > 1 && (
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6B7C73] to-[#AAB2B7]">
                  {' ' + tab.label.split(' ').slice(1).join(' ')}
                </span>
              )}
            </h1>

            {tab.summary && (
              <p className="mt-5 text-lg lg:text-xl text-gray-600 font-body leading-relaxed max-w-3xl">
                {tab.summary}
              </p>
            )}

            {showVerifiedStamp && tab.last_verified_at && (
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2D7E32]/8 border border-[#2D7E32]/15 text-[#2D7E32]">
                <CheckCircle className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold uppercase tracking-wider">
                  Last verified: {formatVerifiedDate(tab.last_verified_at)}
                </span>
              </div>
            )}
          </div>
        </section>

        <article className="container mx-auto px-4 lg:px-8 py-12">
          <TabContentRenderer tab={tab} />
        </article>
      </main>

      <footer className="bg-white border-t border-gray-100 py-10">
        <div className="container mx-auto px-4 text-center">
          <img
            src="/images/carlorbiz-logo.webp"
            alt="Carlorbiz Logo"
            className="h-16 mb-5 mx-auto w-auto object-contain"
          />
          <p className="text-xs text-gray-400 font-body uppercase tracking-wider">
            &copy; Carla Taylor t/as Carlorbiz, {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
}
